import { useEffect, useState } from "react";
import { api, euro, pct } from "../api";
import ExportBar from "../components/ExportBar";

export default function Revenue() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/revenue/latest").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (err) return <div className="text-red-600" data-testid="revenue-error">{err}</div>;
  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.empty) return <div className="bg-white p-8 rounded-xl text-center text-slate-500">No data.</div>;

  return (
    <div data-testid="revenue-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>Revenue Report <span className="text-xs font-normal bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-2">Admin</span></h2>
          <p className="text-sm text-slate-500 mt-0.5">{data.period} · {data.totalHeadcount} resources · Rates configurable in Settings</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExportBar spec={() => ({
            title: "Revenue per Portfolio",
            subtitle: data.period,
            filters: {},
            columns: [
              { key: "portfolio", label: "Portfolio" },
              { key: "headcount", label: "Headcount" },
              { key: "apr", label: "Apr (€)" },
              { key: "may", label: "May (€)" },
              { key: "jun", label: "Jun (€)" },
              { key: "quarter", label: "Quarter (€)" },
              { key: "annual", label: "Annualized (€)" },
            ],
            rows: data.byPortfolio,
            filename: "vSDI_Revenue_Per_Portfolio",
          })} />
        </div>
      </div>

      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))" }}>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-[11px] text-slate-500 font-medium">Monthly Revenue</div>
          <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{euro(data.totals.avgMonthly)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-[11px] text-slate-500 font-medium">Quarter Total</div>
          <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{euro(data.totals.quarter)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-[11px] text-slate-500 font-medium">Annualized</div>
          <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{euro(data.totals.annualized)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-[11px] text-slate-500 font-medium">Total Headcount</div>
          <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{data.totalHeadcount}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-5">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold" style={{ color: "#1a3666" }}>Per Portfolio</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Portfolio</th>
              <th className="px-3 py-2 text-center">Headcount</th>
              <th className="px-3 py-2 text-right">Apr</th>
              <th className="px-3 py-2 text-right">May</th>
              <th className="px-3 py-2 text-right">Jun</th>
              <th className="px-3 py-2 text-right">Quarter</th>
              <th className="px-3 py-2 text-right">Annualized</th>
            </tr>
          </thead>
          <tbody>
            {data.byPortfolio.map((p) => (
              <tr key={p.portfolio} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{p.portfolio}</td>
                <td className="px-3 py-2 text-center">{p.headcount}</td>
                <td className="px-3 py-2 text-right">{euro(p.apr)}</td>
                <td className="px-3 py-2 text-right">{euro(p.may)}</td>
                <td className="px-3 py-2 text-right">{euro(p.jun)}</td>
                <td className="px-3 py-2 text-right font-semibold">{euro(p.quarter)}</td>
                <td className="px-3 py-2 text-right">{euro(p.annual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold flex items-center justify-between flex-wrap gap-2" style={{ color: "#1a3666" }}>
          <span>Per Role</span>
          <ExportBar spec={() => ({
            title: "Revenue per Role",
            subtitle: data.period,
            filters: {},
            columns: [
              { key: "label", label: "Role" },
              { key: "rate", label: "Monthly Rate (€)" },
              { key: "headcount", label: "Headcount" },
              { key: "apr", label: "Apr (€)" },
              { key: "may", label: "May (€)" },
              { key: "jun", label: "Jun (€)" },
              { key: "quarter", label: "Quarter (€)" },
              { key: "annual", label: "Annualized (€)" },
              { key: "utilizedFte", label: "Effective FTE" },
            ],
            rows: data.byRole,
            filename: "vSDI_Revenue_Per_Role",
          })} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-right">Monthly Rate</th>
              <th className="px-3 py-2 text-center">Headcount</th>
              <th className="px-3 py-2 text-right">Quarter</th>
              <th className="px-3 py-2 text-right">Annualized</th>
              <th className="px-3 py-2 text-center">Eff FTE</th>
            </tr>
          </thead>
          <tbody>
            {data.byRole.map((r) => (
              <tr key={r.role} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{r.label}</td>
                <td className="px-3 py-2 text-right">{euro(r.rate)}</td>
                <td className="px-3 py-2 text-center">{r.headcount}</td>
                <td className="px-3 py-2 text-right font-semibold">{euro(r.quarter)}</td>
                <td className="px-3 py-2 text-right">{euro(r.annual)}</td>
                <td className="px-3 py-2 text-center">{pct(r.utilizedFte / r.headcount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
